import { Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule, ControlValueAccessor, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormMask, FormValidatorMessages } from '../../core/enums/forms.enums';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CountryCodeDropdown } from "../country-code-dropdown/country-code-dropdown.component";
import { CommonChecks } from '../../core/utilities/common-checks.utilities';
import { BaseInputComponentClass } from '../../core/base-classes/base-input-component-class';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'ms-phone-input',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgxMaskDirective, CountryCodeDropdown],
  templateUrl: './phone-input.component.html',
  providers: [
    provideNgxMask(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInput),
      multi: true
    }
  ]
})
export class PhoneInput extends BaseInputComponentClass implements OnInit, OnDestroy, ControlValueAccessor {

  //#region Properties

  public FormMask = FormMask;
  public countryCode = '1';
  public phoneNumber = '';
  public extension = '';
  public phoneControl = new FormControl('');

  private destroy$ = new Subject<void>();

  //#endregion Properties

  //#region Initialization & Cleanup

  public override ngOnInit(): void {
    super.ngOnInit();

    this.phoneControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const digitsOnly = (value || '').replace(/\D/g, '');
        if (digitsOnly !== this.phoneNumber) {
          this.phoneNumber = digitsOnly;
          this.onChange(this.buildPhoneData());
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //#endregion Initialization & Cleanup

  //#region ControlValueAccessor

  public writeValue(value: any): void {
    if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
      this.countryCode = '1';
      this.phoneNumber = '';
      this.extension = '';
      this.phoneControl.setValue('', { emitEvent: false });
    } else {
      this.countryCode = value.countryCode ?? '1';
      this.phoneNumber = value.phoneNumber ?? '';
      this.extension = value.extension ?? '';
      this.phoneControl.setValue(this.phoneNumber, { emitEvent: false });
    }
  }

  //#endregion ControlValueAccessor

  //#region State Getters

  public get isValid(): boolean {
    return this.touched
      && !CommonChecks.isNullOrUndefinedOrEmpty(this.phoneNumber)
      && this.ngControl?.control?.errors == null;
  }

  //#endregion State Getters

  //#region Event Handlers

  public onCountryCodeChange(newCountryCode: string): void {
    this.countryCode = newCountryCode;

    if (this.phoneNumber) {
      this.onChange(this.buildPhoneData());
    }
  }

  public onExtensionInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const digitsOnly = target.value.replace(/\D/g, '');

    if (digitsOnly.length <= 10) {
      this.extension = digitsOnly;
      this.onChange(this.buildPhoneData());
    }
  }

  //#endregion Event Handlers

  //#region Validation

  public getErrors(): string[] {
    const errors: string[] = [];
    const controlErrors = this.ngControl?.control?.errors;

    if (controlErrors) {
      if (controlErrors['required']) {
        errors.push(controlErrors['required'].message || FormValidatorMessages.PhoneRequired);
      }
      if (controlErrors['phone']) {
        errors.push(controlErrors['phone'].message || FormValidatorMessages.PhoneInvalid);
      }
    }

    return errors;
  }

  //#endregion Validation

  //#region Private Methods

  private buildPhoneData(): { countryCode: string; phoneNumber: string; extension: string } {
    return {
      countryCode: this.countryCode,
      phoneNumber: this.phoneNumber,
      extension: this.extension
    };
  }

  //#endregion Private Methods
}
